import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useSnackbar } from 'notistack';
import apiClient from '../../../../../api/apiClient';
import { usePreloaderStore } from '../../../../../stores';
import Modal from '../../controls/Modal';
import LabeledInput from '../../controls/LabeledInput';
import SpiteToggle from './SpiteToggle';

// Модалка создания/редактирования категории заданий с лимитами (соотношение, дневной).
export default function CategoryModal({ category, neighborId, onSuccess, onClose }) {
    const editing = !!category;
    const { enqueueSnackbar } = useSnackbar();
    const { show, hide } = usePreloaderStore();
    const [name, setName] = useState(category?.displayName || '');
    const [ratioOn, setRatioOn] = useState(category?.ratioLimit != null);
    const [ratio, setRatio] = useState(String(category?.ratioLimit ?? 1.5));
    const [dailyOn, setDailyOn] = useState(category?.dailyLimit != null);
    const [daily, setDaily] = useState(String(category?.dailyLimit ?? 2));
    const [notify, setNotify] = useState(category?.notifyOrganizersOnPurchase || false);

    const submit = async () => {
        if (!name.trim()) { enqueueSnackbar('Введите название категории', { variant: 'warning' }); return; }
        const ratioLimit = ratioOn ? parseFloat(ratio) : null;
        const dailyLimit = dailyOn ? parseInt(daily, 10) : null;
        show();
        try {
            if (editing) {
                await apiClient.patch(`/api/neighbor/admin/task-categories/rename?id=${category.id}`, { displayName: name.trim() });
                await apiClient.patch(`/api/neighbor/admin/task-categories/update-limits?id=${category.id}`, { ratioLimit, dailyLimit, notifyOrganizersOnPurchase: notify });
                enqueueSnackbar('Категория обновлена', { variant: 'success' });
            } else {
                await apiClient.post('/api/neighbor/admin/task-categories/create', { neighborId, displayName: name.trim(), ratioLimit, dailyLimit, notifyOrganizersOnPurchase: notify });
                enqueueSnackbar('Категория создана', { variant: 'success' });
            }
            onSuccess();
            onClose();
        } catch (e) { enqueueSnackbar(e.response?.data?.message || 'Ошибка при сохранении', { variant: 'error' }); }
        finally { hide(); }
    };

    const del = async () => {
        if (!window.confirm(`Удалить категорию «${category.displayName}»? Все задания в ней будут удалены.`)) return;
        show();
        try {
            await apiClient.delete(`/api/neighbor/admin/task-categories/delete?id=${category.id}`);
            enqueueSnackbar('Категория удалена', { variant: 'success' });
            onSuccess();
            onClose();
        } catch (e) { enqueueSnackbar('Ошибка при удалении', { variant: 'error' }); }
        finally { hide(); }
    };

    const footer = (
        <>
            {editing && <button type="button" className="btn btn-danger" style={{ marginRight: 'auto' }} onClick={del}>Удалить</button>}
            <button type="button" className="btn btn-ghost" onClick={onClose}>Отмена</button>
            <button type="button" className="btn btn-primary" onClick={submit}>{editing ? 'Сохранить' : 'Создать'}</button>
        </>
    );

    return (
        <Modal open kicker="Категория" title={editing ? 'Редактировать категорию' : 'Создать категорию'} onClose={onClose} footer={footer}>
            <LabeledInput label="Название категории" value={name} onChange={(e) => setName(e.target.value)} />
            <div className="sp-limgrid">
                <div className={`sp-lim${ratioOn ? ' on' : ''}`}>
                    <div className="sp-lim-head">
                        <h3>Лимит соотношения</h3>
                        <button type="button" className={`sp-switch${ratioOn ? ' on' : ''}`} onClick={() => setRatioOn(!ratioOn)}><span className="sw" /></button>
                    </div>
                    <div className="d">Минимум заданий из других категорий на одно из этой.</div>
                    <div className="hint">Например: 2 = нужно купить 2 других, чтобы купить 1 из этой</div>
                    {ratioOn && <div className="sp-lim-field"><LabeledInput label="Коэффициент" type="number" value={ratio} onChange={(e) => setRatio(e.target.value)} /></div>}
                </div>
                <div className={`sp-lim${dailyOn ? ' on' : ''}`}>
                    <div className="sp-lim-head">
                        <h3>Дневной лимит</h3>
                        <button type="button" className={`sp-switch${dailyOn ? ' on' : ''}`} onClick={() => setDailyOn(!dailyOn)}><span className="sw" /></button>
                    </div>
                    <div className="d">Сколько раз в день можно купить одно и то же задание в этой категории.</div>
                    {dailyOn && <div className="sp-lim-field"><LabeledInput label="Количество" type="number" value={daily} onChange={(e) => setDaily(e.target.value)} /></div>}
                </div>
            </div>
            <div className="sp-sec">
                <div className="sp-tgs">
                    <SpiteToggle label="Уведомлять организаторов о покупке" checked={notify} onChange={() => setNotify(!notify)} full />
                </div>
                <div className="sp-notify-hint">
                    При покупке задания из этой категории оргам уходит уведомление в Telegram-канал.
                    Если категория скопирована из пака — «Синхронизировать с паком» перезапишет флаг значением из пака;
                    для постоянного включения включайте его в самом паке.
                </div>
            </div>
        </Modal>
    );
}

CategoryModal.propTypes = {
    category: PropTypes.object,
    neighborId: PropTypes.number,
    onSuccess: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired
};

CategoryModal.defaultProps = { category: null, neighborId: null };
